import { CSSProperties, FC } from 'react';

import { keyframes, styled } from '../theme';

const shimmer = keyframes({
    '0%': { backgroundPosition: '100% 0' },
    '100%': { backgroundPosition: '0 0' },
});

const Box = styled.div(({ theme }) => ({
    borderRadius: theme.borderRadius.base,
    backgroundImage: `linear-gradient(90deg, ${theme.color.backgroundGray} 25%, ${theme.color.white} 37%, ${theme.color.backgroundGray} 63%)`,
    backgroundSize: '400% 100%',
    animation: `${shimmer} 1.4s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
}));

type Props = {
    /** Defaults to `100%`. */
    width?: number | string;
    /** Defaults to `16`. */
    height?: number | string;
    /** Override the default (theme) corner radius. Ignored when `circle`. */
    borderRadius?: number | string;
    /** Render a circular placeholder (e.g. an avatar). */
    circle?: boolean;
    className?: string;
    style?: CSSProperties;
};

/**
 * Shimmering placeholder block for loading states. Compose several to mirror
 * the shape of the content being loaded. Respects `prefers-reduced-motion`.
 */
const Skeleton: FC<Props> = ({
    width = '100%',
    height = 16,
    borderRadius,
    circle = false,
    className,
    style,
}) => {
    const radius = circle ? '50%' : borderRadius;
    return (
        <Box
            className={className}
            style={{ width, height, ...(radius != null && { borderRadius: radius }), ...style }}
        />
    );
};

export default Skeleton;
